// app/javascript/controllers/waiting_lottie_controller.js
import { Controller } from "@hotwired/stimulus"
import lottie from "lottie-web"
import { gsap } from "gsap"

// Lightweight wrapper around lottie-web to play a named JSON animation.
// Usage (recommended):
// <span data-controller="waiting-lottie" data-waiting-lottie-name-value="wand"></span>
// Then call controller.play("wand") / controller.stop()
export default class extends Controller {
  static values = {
    name: String,
    urls: Object
  }
  static _jsonCache = new Map()

  connect() {
    this._frontAnim = null
    this._backAnim = null
    this._frontEl = document.createElement("span")
    this._backEl = document.createElement("span")

    // Host element should keep its space and not collapse.
    this.element.style.display = "inline-block"
    this.element.style.position = this.element.style.position || "relative"
    this.element.style.overflow = "hidden"

    // Ensure the host has a real box size. If it's only constrained by max-width/max-height,
    // `getBoundingClientRect()` can be 0 until layout, and absolutely positioned children won't render.
    try {
      const cs = window.getComputedStyle(this.element)
      const rect = this.element.getBoundingClientRect()
      const maxW = cs.maxWidth && cs.maxWidth !== "none" ? cs.maxWidth : null
      const maxH = cs.maxHeight && cs.maxHeight !== "none" ? cs.maxHeight : null

      if (rect.width === 0 && !this.element.style.width) {
        this.element.style.width = maxW || "75px"
      }
      if (rect.height === 0 && !this.element.style.height) {
        this.element.style.height = maxH || "75px"
      }
    } catch (_) {
      // Safe defaults
      if (!this.element.style.width) this.element.style.width = "75px"
      if (!this.element.style.height) this.element.style.height = "75px"
    }

    // Layer the two containers.
    ;[this._frontEl, this._backEl].forEach((el) => {
      el.style.position = "absolute"
      el.style.left = "0"
      el.style.top = "0"
      el.style.width = "100%"
      el.style.height = "100%"
    })

    this._frontEl.style.opacity = "1"
    this._backEl.style.opacity = "0"
    this._swapToken = 0

    // Clean and mount.
    this.element.innerHTML = ""
    this.element.appendChild(this._frontEl)
    this.element.appendChild(this._backEl)

    if (this.hasNameValue && this.nameValue) {
      this.play(this.nameValue)
    }
  }

  async play(name) {
    if (!name) return

    const nextName = String(name)

    // Token to ignore stale async completions when multiple play() calls happen quickly.
    const myToken = ++this._swapToken

    // If switching to a different animation, hide the current one immediately to avoid mismatch
    // where the text changes but the previous lottie is still visible.
    if (this._currentName && this._currentName !== nextName) {
      try {
        gsap.killTweensOf([this._frontEl, this._backEl])
        gsap.to(this._frontEl, { opacity: 0, duration: 0.12, ease: "power1.out" })
        gsap.set(this._backEl, { opacity: 0 })
      } catch (_) {
        try { this._frontEl.style.opacity = "0" } catch (_) {}
        try { this._backEl.style.opacity = "0" } catch (_) {}
      }
    }

    if (this._currentName === nextName && this._frontAnim) {
      try { this._frontAnim.play?.() } catch (_) {}
      return
    }
    this._currentName = nextName

    const urls = this.hasUrlsValue ? this.urlsValue : null
    const url = urls ? (urls[nextName] || urls[String(nextName)]) : null
    if (!url) return

    // Get JSON (cached)
    let animationData = this.constructor._jsonCache.get(url)
    if (!animationData) {
      const resp = await fetch(url, { credentials: "same-origin" })
      if (!resp.ok) return
      animationData = await resp.json()
      this.constructor._jsonCache.set(url, animationData)
    }

    // Destroy any prior back animation
    try { this._backAnim?.destroy?.() } catch (_) {}
    this._backAnim = null
    try { this._backEl.innerHTML = "" } catch (_) {}

    // Load into back layer (hidden initially)
    this._backAnim = lottie.loadAnimation({
      container: this._backEl,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData
    })

    // When ready, crossfade swap
    const finalizeSwap = () => {
      if (myToken !== this._swapToken) return
      try { this._backAnim.removeEventListener?.("DOMLoaded", finalizeSwap) } catch (_) {}

      try { gsap.killTweensOf([this._frontEl, this._backEl]) } catch (_) {}

      // Ensure correct starting opacities
      gsap.set(this._backEl, { opacity: 0 })

      const currentFrontOpacity = parseFloat(window.getComputedStyle(this._frontEl).opacity || "1")
      if (currentFrontOpacity > 0) {
        gsap.to(this._frontEl, { opacity: 0, duration: 0.18, ease: "power1.out" })
      }
      gsap.to(this._backEl, {
        opacity: 1,
        duration: 0.30,
        ease: "power1.out",
        onComplete: () => {
          // Destroy old front animation and clear its container
          try { this._frontAnim?.destroy?.() } catch (_) {}
          this._frontAnim = null
          try { this._frontEl.innerHTML = "" } catch (_) {}

          // Swap refs: back becomes new front
          ;[this._frontEl, this._backEl] = [this._backEl, this._frontEl]
          ;[this._frontAnim, this._backAnim] = [this._backAnim, null]

          // Restore baseline
          this._frontEl.style.opacity = "1"
          this._backEl.style.opacity = "0"
        }
      })
    }

    // Prefer DOMLoaded; if unavailable, swap shortly after start
    try {
      this._backAnim.addEventListener?.("DOMLoaded", finalizeSwap)
      // fallback timeout in case event doesn't fire in some builds
      setTimeout(() => {
        if (myToken !== this._swapToken) return
        if (this._backAnim) finalizeSwap()
      }, 150)
    } catch (_) {
      setTimeout(finalizeSwap, 150)
    }
  }

  stop() {
    try { this._frontAnim?.destroy?.() } catch (_) {}
    try { this._backAnim?.destroy?.() } catch (_) {}
    this._frontAnim = null
    this._backAnim = null
    this._currentName = null

    try { this._frontEl && (this._frontEl.innerHTML = "") } catch (_) {}
    try { this._backEl && (this._backEl.innerHTML = "") } catch (_) {}

    try {
      gsap.set(this._frontEl, { opacity: 1 })
      gsap.set(this._backEl, { opacity: 0 })
    } catch (_) {}
  }

  disconnect() {
    this.stop()
  }
}